// Demo handoff data only. Risk assessment and issue resolution belong to case management.
export function passportIssue(
  availability: {
    reason: string;
    applicationStatus?: string;
    applicationDate?: string;
    applicationDateIsApproximate?: boolean;
    expedited?: string;
    expectedDate?: string;
  },
  assessment: { status: string; reason: string | null } | null = null,
  requiredDate = "",
) {
  if (!availability?.reason) return null;
  return {
    type: availability.reason === "expiring_soon" ? "passport_validity_review" : "passport_required",
    status: "open",
    owner: "case_management",
    reason: availability.reason,
    applicationStatus:
      availability.applicationStatus
      || (["first_pending", "replacement_pending"].includes(availability.reason) ? "yes" : availability.reason === "not_applied" ? "no" : null),
    applicationDate: availability.applicationDate || null,
    applicationDateIsApproximate: availability.applicationDate ? (availability.applicationDateIsApproximate ?? null) : null,
    expedited: availability.expedited || null,
    expectedReceiptDate: availability.expectedDate || null,
    risk: assessment ?? renewalRisk(availability, requiredDate),
  };
}

export const passportIssueScenarios = [
  {
    name: "Passport not applied for, near departure",
    daysUntilRequired: 21,
    availability: { reason: "not_applied" },
    assessment: { status: "at_risk", reason: "Passport not yet applied for; required on location in 21 days." },
  },
  {
    name: "Passport pending, production six months away",
    daysUntilRequired: 180,
    availability: { reason: "first_pending", dateUnknown: true },
    assessment: { status: "not_at_risk", reason: "Demo assessment: sufficient time remains; passport requirement stays open." },
  },
  {
    name: "Expired passport, renewal not started",
    availability: { reason: "expired" },
    assessment: { status: "unassessed", reason: null },
  },
  {
    name: "Passport expiring soon, no scan available",
    availability: { reason: "expiring_soon" },
    assessment: { status: "unassessed", reason: null },
  },
];

// Calendar days, not elapsed hours: avoid daylight-saving and time-of-day drift.
export function daysUntilRequired(requiredDate: string | undefined, today = new Date().toLocaleDateString("en-CA")): number | null {
  const parse = (value: string | undefined) => {
    if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return NaN;
    const time = Date.parse(value + "T00:00:00Z");
    return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value ? time : NaN;
  };
  const days = (parse(requiredDate) - parse(today)) / 86400000;
  return Number.isFinite(days) ? days : null;
}

export function renewalRisk(
  value: { reason: string; applicationStatus?: string },
  requiredDate?: string,
  today?: string,
): { status: string; reason: string | null } {
  const days = daysUntilRequired(requiredDate, today);
  // Expiry alone does not establish that renewal is required for this trip.
  if (value.reason === "expired" && value.applicationStatus === "no" && days !== null && days <= 60) {
    return {
      status: "at_risk",
      reason:
        days > 0
          ? `Passport renewal not started; required on location in ${days} days.`
          : "Passport renewal not started; required arrival date reached or passed.",
    };
  }
  return { status: "unassessed", reason: null };
}

export function renewalNotice(
  value: { reason: string; applicationStatus?: string },
  requiredDate?: string,
  locale = "en",
  today?: string,
  destination = "",
): { urgent: boolean; title: string; text: string; followUp?: string } | null {
  if (value.applicationStatus !== "no" || !["expired", "expiring_soon", "no_passport", "lost_stolen"].includes(value.reason)) return null;
  const es = locale === "es";
  const days = daysUntilRequired(requiredDate, today);
  const urgent = days !== null && days <= 60;
  if (["no_passport", "lost_stolen"].includes(value.reason)) {
    const replacement = value.reason === "lost_stolen";
    return {
      urgent,
      title: es ? (replacement ? "Solicite un pasaporte de reemplazo" : "Solicite su pasaporte") : replacement ? "Request a replacement passport" : "Apply for your passport",
      text: es
        ? urgent
          ? `${days !== null && days > 0 ? `Debe llegar al lugar de rodaje en ${days} días.` : "La fecha de llegada prevista ya se ha alcanzado."} Inicie el trámite cuanto antes y consulte si hay un trámite urgente disponible.`
          : "Inicie el trámite pronto para disponer de tiempo para recibir su pasaporte y completar los trámites migratorios."
        : urgent
          ? `${days !== null && days > 0 ? `You’re due on location in ${days} days.` : "Your required arrival date has been reached."} Apply promptly and check whether expedited processing is available.`
          : "Apply soon to allow time for your passport and any immigration processing.",
    };
  }

  const countryMap: Record<string, string> = {
    US: es ? "Estados Unidos" : "the United States",
    ES: es ? "España" : "Spain",
    IT: es ? "Italia" : "Italy",
    CO: "Colombia",
  };
  const country = countryMap[destination];
  const formattedDate =
    days === null || !requiredDate
      ? ""
      : new Intl.DateTimeFormat(es ? "es" : "en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" }).format(
          new Date(requiredDate + "T00:00:00Z"),
        );
  const timing =
    days === null
      ? ""
      : es
        ? `Su llegada ${country ? `a ${country}` : "al lugar de rodaje"} está prevista para el ${formattedDate}${days > 0 ? `, en ${days} días` : days === 0 ? ", hoy" : ", fecha que ya ha pasado"}. `
        : `You’re due ${country ? `in ${country}` : "on location"} on ${formattedDate}${days > 0 ? `, in ${days} days` : days === 0 ? ", today" : ", a date that has already passed"}. `;
  const expiring = value.reason === "expiring_soon";
  const guidance = es
    ? expiring
      ? "Compruebe cuanto antes si necesita renovar su pasaporte para este viaje. Si necesita renovarlo, consulte si hay un trámite urgente disponible."
      : urgent
        ? "Solicite la renovación cuanto antes y consulte si hay un trámite urgente disponible a través de la autoridad que expide su pasaporte."
        : "Inicie la renovación pronto."
    : expiring
      ? "Check promptly whether your passport needs renewing for this trip. If renewal is needed, check whether expedited processing is available through your passport authority."
      : urgent
        ? "Request your renewal as soon as possible and check whether expedited processing is available through your passport authority."
        : "Start your passport renewal soon.";
  return {
    urgent,
    title: es
      ? expiring
        ? "Revise la vigencia de su pasaporte"
        : urgent
          ? "Inicie la renovación ahora"
          : "Renueve su pasaporte"
      : expiring
        ? "Check your passport validity"
        : urgent
          ? "Start your passport renewal now"
          : "Arrange your passport renewal",
    text:
      timing
      + guidance
      + (es
        ? " Deje tiempo para recibir el pasaporte y completar los trámites migratorios necesarios."
        : " Allow time for passport delivery and any required immigration processing."),
    followUp: es
      ? "Una vez solicitada la renovación, vuelva aquí para actualizar su estado."
      : "Once you’ve requested renewal, return here to update your status.",
  };
}

export function passportNotStartedLabel(value: { reason: string; applicationStatus?: string }, locale = "en"): string {
  if (value.applicationStatus !== "no") return "";
  const labels: Record<string, [string, string]> = {
    expired: ["Renewal not started", "Renovación no iniciada"],
    no_passport: ["Passport not yet requested", "Pasaporte aún no solicitado"],
    lost_stolen: ["Replacement not yet requested", "Reemplazo aún no solicitado"],
    expiring_soon: ["Passport validity needs review", "Vigencia pendiente de revisión"],
  };
  return labels[value.reason]?.[locale === "es" ? 1 : 0] || "";
}
